import { useTranslation } from 'react-i18next';
import { useFormContext, SubmitErrorHandler } from 'react-hook-form';
import { useDispatch, useSelector } from '@cbridge-utils/react-redux';
import { useHasChanges } from '@cbridge-tools/form';
import { PolicyProtection } from '@cbridge-packages/auth';
import { DetailsSidebarAction, detailsSidebarModeSelector, SidebarOpenMode } from '@cbridge-packages/details-sidebar';
import { useConfirmCancelForm } from '@cbridge-modules/unsaved-changes-confirmation';
import { DefaultDetailsSidebarViewControls } from '../DefaultDetailsSidebarViewControls/DefaultDetailsSidebarViewControls';
import { DetailsSidebarEditControls } from '../DetailsSidebarEditControls/DetailsSidebarEditControls';
import { DetailsSidebarFormActionsProps } from './DetailsSidebarFormActions.interface';

export const DetailsSidebarFormActions = <T, >(props: DetailsSidebarFormActionsProps<T>) => {
  const { onCreate, onUpdate, onCancelCreate, onCancelUpdate } = props;

  const { t } = useTranslation();

  const mode = useSelector(detailsSidebarModeSelector);
  const isNew = mode === SidebarOpenMode.Create || mode === SidebarOpenMode.Copy;
  const isEdit = mode === SidebarOpenMode.Edit;

  const dispatch = useDispatch();

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, isValid, isSubmitted },
  } = useFormContext<T>();

  const { confirmCloseForm } = useConfirmCancelForm();

  const hasChanges = useHasChanges();

  const handleCancelUpdate = async () => {
    if (!hasChanges || (await confirmCloseForm())) {
      reset();
      if (isNew) {
        if (onCancelCreate) {
          onCancelCreate();
        } else {
          dispatch(DetailsSidebarAction.close());
        }
      } else {
        if (onCancelUpdate) {
          onCancelUpdate();
        } else {
          dispatch(DetailsSidebarAction.toggleEdit());
        }
      }
    }
  };

  const handleError: SubmitErrorHandler<T> = (errors) => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(JSON.stringify(errors));
    }
  };

  if (mode === undefined) {
    return null;
  }

  if (isNew) {
    return (
      <DetailsSidebarEditControls
        loading={isSubmitting}
        disabled={isSubmitted && !isValid}
        submitButtonLabel={t('text:common.button.create')}
        onSubmit={handleSubmit(onCreate, handleError)}
        onCancel={handleCancelUpdate}
      />
    );
  }

  if (isEdit) {
    return (
      <DetailsSidebarEditControls
        loading={isSubmitting}
        disabled={(isSubmitted && !isValid) || !hasChanges}
        onSubmit={handleSubmit(onUpdate, handleError)}
        onCancel={handleCancelUpdate}
      />
    );
  }

  return (
    <PolicyProtection>
      <DefaultDetailsSidebarViewControls />
    </PolicyProtection>
  );
};
